import React, { useState, useMemo, useCallback } from 'react';
import { FiSearch, FiFilter, FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { TopBar } from '@/design-system/components/organisms/TopBar';
import { DataTable } from '@/features/onboarding/components/DataTable';
import { FilterBadge } from '@/features/onboarding/components/FilterBadge';
import { FilterModal, type FilterData } from '@/features/onboarding/components/FilterModal';
import { Sidebar } from '@/features/onboarding/components/Sidebar';
import { usePreAnalysisClients, usePrefetchPreAnalysis } from '@/features/onboarding/hooks';
import type { PreClient, PreClientQueryParams } from '@/features/onboarding/types';
import { ONBOARDING_SIDEBAR_ITEMS } from '@/shared/constants/sidebar.constants';

interface ActiveFilter {
  key: string;
  label: string;
  value: string;
}

export const PreAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dateSearch, setDateSearch] = useState<string | undefined>(undefined);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Build query params dynamically
  const queryParams = useMemo<PreClientQueryParams>(() => {
    const params: PreClientQueryParams = {
      page: currentPage,
      limit: itemsPerPage,
    };

    // Only add search if it has a value
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    // Only add dateSearch if it has a value
    if (dateSearch) {
      params.dateSearch = dateSearch;
    }

    return params;
  }, [currentPage, itemsPerPage, searchQuery, dateSearch]);

  // Fetch pre-clients with React Query
  const { data, isLoading, isError, error } = usePreAnalysisClients(queryParams);

  // Prefetch next page for better UX
  const prefetchNextPage = usePrefetchPreAnalysis(queryParams);

  // Build active filters for display
  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const filters: ActiveFilter[] = [];

    if (dateSearch) {
      const formattedDate = new Date(dateSearch).toLocaleDateString('pt-BR');
      filters.push({
        key: 'dateSearch',
        label: `Data: ${formattedDate}`,
        value: dateSearch,
      });
    }

    return filters;
  }, [dateSearch]);

  // Handle filter removal
  const handleRemoveFilter = useCallback((filterKey: string) => {
    if (filterKey === 'dateSearch') {
      setDateSearch(undefined);
    }
    // Reset to first page when removing filters
    setCurrentPage(1);
  }, []);

  // Handle filter application from modal
  const handleApplyFilters = useCallback((filterData: FilterData) => {
    // Apply dateSearch filter if provided
    const { startDate } = filterData;
    if (startDate) {
      // Format date to YYYY-MM-DD for API
      const date = new Date(startDate);
      const formattedDate = date.toISOString().split('T')[0];
      setDateSearch(formattedDate);
    } else {
      setDateSearch(undefined);
    }

    // Reset to first page when applying new filters
    setCurrentPage(1);
    setIsFilterModalOpen(false);
  }, []);

  // Handle search with debounce reset
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    // Reset to first page when searching
    setCurrentPage(1);
  }, []);

  // Handle page change with prefetch
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Prefetch next page when user navigates
    if (data?.pagination && page < data.pagination.totalPages) {
      prefetchNextPage();
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = useCallback((limit: number) => {
    setItemsPerPage(limit);
    // Reset to first page when changing items per page
    setCurrentPage(1);
  }, []);

  const columns = [
    {
      key: 'name',
      label: 'Cliente',
      width: '18%',
    },
    {
      key: 'openingDate',
      label: 'Data de abertura',
      width: '12%',
      render: (value: unknown) => {
        if (!value) return '-';
        const date = new Date(value as string);
        return date.toLocaleDateString('pt-BR');
      },
    },
    {
      key: 'createdAt',
      label: 'SLA de aprovação',
      width: '12%',
      render: (value: unknown) => {
        if (!value) return '-';
        const createdDate = new Date(value as string);
        const now = new Date();
        const diffMs = now.getTime() - createdDate.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        let bgColor = 'bg-green-100 text-green-700';
        if (diffHours >= 72) {
          bgColor = 'bg-red-100 text-red-700';
        } else if (diffHours >= 48) {
          bgColor = 'bg-orange-100 text-orange-700';
        } else if (diffHours >= 24) {
          bgColor = 'bg-yellow-100 text-yellow-700';
        }

        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}>
            {diffHours}h
          </span>
        );
      },
    },
    {
      key: 'numberDocument',
      label: 'CPF/CNPJ',
      width: '14%',
      render: (value: unknown) => {
        const doc = value as string;
        if (!doc) return '-';
        // Format CPF (11 digits) or CNPJ (14 digits)
        if (doc.length === 11) {
          return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        if (doc.length === 14) {
          return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return doc;
      },
    },
    {
      key: 'aliasName',
      label: 'Sócio administrador',
      width: '15%',
      render: (value: unknown) => (value as string) || '-',
    },
    {
      key: 'email',
      label: 'Email',
      width: '18%',
    },
    {
      key: 'actions',
      label: 'Ações',
      width: '6%',
      render: (_: unknown, row: PreClient) => (
        <button
          type="button"
          onClick={() => {
            void navigate(`/onboarding/client/${row.id}`);
          }}
          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
        >
          <FiChevronRight className="w-5 h-5" />
        </button>
      ),
    },
  ];

  // Get data from API response or use empty defaults
  const clients = data?.data ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 10, totalItems: 0, totalPages: 1 };

  return (
    <div className="min-h-screen bg-neutral-50">
      <TopBar />

      <div className="flex">
        {/* Sidebar */}
        <Sidebar items={ONBOARDING_SIDEBAR_ITEMS} className="w-64 min-h-[calc(100vh-140px)]" />

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            {/* Search and Filters */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Buscar por cliente ou CPF/CNPJ"
                  value={searchQuery}
                  onChange={(e) => {
                    handleSearchChange(e.target.value);
                  }}
                  className="w-full pl-12 pr-4 py-3 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsFilterModalOpen(true);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-purple-600 text-neutral-700 rounded-lg hover:bg-purple-50 transition-colors font-medium"
              >
                <FiFilter className="w-5 h-5 text-neutral-600" />
                Data
              </button>
            </div>

            {/* Active Filters */}
            {activeFilters.length > 0 && (
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                {activeFilters.map((filter) => (
                  <FilterBadge
                    key={filter.key}
                    label={filter.label}
                    onRemove={() => {
                      handleRemoveFilter(filter.key);
                    }}
                  />
                ))}
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-neutral-600">Carregando pré-clientes...</span>
              </div>
            )}

            {/* Error State */}
            {isError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                <p className="font-medium">Erro ao carregar pré-clientes</p>
                <p className="text-sm">
                  {error instanceof Error ? error.message : 'Tente novamente mais tarde.'}
                </p>
              </div>
            )}

            {/* Data Table */}
            {!isLoading && !isError && (
              <DataTable
                columns={columns}
                data={clients}
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                itemsPerPage={pagination.limit}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}
          </div>
        </main>
      </div>

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => {
          setIsFilterModalOpen(false);
        }}
        onApply={handleApplyFilters}
      />
    </div>
  );
};
